import { useState, useRef, useLayoutEffect, ReactNode } from 'react';

import { HorizontalMenuTabs } from '@backrs_ui/common-ui';

import {
  MakeItPersonal,
  MakeItPersonalProps,
} from '@backrs_ui/grow-ui/src/components/MakeItPersonal/MakeItPersonal';

import { Box, Theme, SxProps } from '@mui/material';

import { ResourcePageSection } from './types';

type ResourceWrapperProps = Readonly<{
  header: ReactNode;
  sections: ResourcePageSection[];
  chipLabel: string;
  makeItPersonalProps: MakeItPersonalProps;
}>;

export const ResourceWrapper = ({
  header,
  sections,
  chipLabel,
  makeItPersonalProps,
}: ResourceWrapperProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isAutoScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const TAB_TITLES = sections.map((section) => section.title);
  const TAB_KEYS = sections.map((section) => section.id);

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      if (isAutoScrollingRef.current) return;

      const scrollPosition = contentRef.current.scrollTop + 150;
      const isAtBottom =
        contentRef.current.scrollTop + contentRef.current.clientHeight >=
        contentRef.current.scrollHeight - 10;
      const isAtTop = contentRef.current.scrollTop < 100;

      // If we're near the top of the page, set tabIndex to -1
      if (isAtTop) {
        setTabIndex(-1);

        return;
      }

      // If we're at the bottom of the page, set tabIndex to the last section
      if (isAtBottom && sections.length > 0) {
        setTabIndex(sections.length - 1);

        return;
      }

      const newIndex = TAB_KEYS.findIndex((sectionId) => {
        const section = document.getElementById(sectionId);

        if (section && contentRef.current) {
          const sectionTop = section.offsetTop - contentRef.current.offsetTop;

          return scrollPosition >= sectionTop && scrollPosition < sectionTop + section.offsetHeight;
        }

        return false;
      });

      if (newIndex !== -1) {
        setTabIndex(newIndex);
      }
    };

    const container = contentRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [TAB_KEYS, sections.length]);

  const handleTabChange = (newIndex: number) => {
    setTabIndex(newIndex);

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    isAutoScrollingRef.current = true;

    if (newIndex === -1) {
      // Scroll to the very top of the page on chip click
      contentRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      const element = document.getElementById(TAB_KEYS[newIndex]);

      if (element && contentRef.current) {
        const sectionTop = element.offsetTop - contentRef.current.offsetTop;

        contentRef.current.scrollTo({
          top: sectionTop - 48, // height of the tabs
          behavior: 'smooth',
        });
      }
    }

    scrollTimeoutRef.current = setTimeout(() => {
      isAutoScrollingRef.current = false;
    }, 750);
  };

  return (
    <Box sx={containerSx}>
      <Box sx={mainContentSx} ref={contentRef}>
        {header}

        <HorizontalMenuTabs
          setValue={handleTabChange}
          tabs={TAB_TITLES}
          value={tabIndex}
          chipConfig={{
            label: chipLabel,
            iconName: 'ArrowUp',
            iconColor: 'Colors/Foreground/fg-quaternary',
            onClick: () => handleTabChange(-1),
          }}
          variant="minimal"
        />

        <Box sx={contentContainerSx}>
          {sections.map((section) => (
            <Box key={section.id} id={section.id}>
              {section.component}
            </Box>
          ))}
        </Box>
      </Box>

      <MakeItPersonal
        pageName={makeItPersonalProps.pageName}
        badges={makeItPersonalProps.badges}
        activeBadge={makeItPersonalProps.activeBadge}
        onBadgeClick={makeItPersonalProps.onBadgeClick}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
  display: {
    xs: 'block',
    md: 'flex',
  },
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  height: '100dvh',
};

const mainContentSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  '@media (max-height: 768px)': {
    ml: '72px',
    maxWidth: '100%',
  },
  '@media (max-width: 414px)': {
    ml: '0px',
    maxWidth: '100%',
  },
  '@media (max-width: 900px)': {
    ml: '0px',
    maxWidth: '100%',
  },
  overflowY: 'auto',
};

const contentContainerSx: SxProps<Theme> = {
  maxWidth: '936px !important',
  alignSelf: 'center',
  flex: 1,
  width: '100%',
};
